import { cx } from '@linaria/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, cloneElement, useState, useEffect } from 'react';
import { getPageContent } from '@pm2/api';
import { useContentEnginePrinters, ContentEnginePrinterProvider, parsePageXML } from '@pm2/content-engine';
import { pm2SitemapStore, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useLocation } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { Tooltip, Switch } from '@ui/kit';
import { generateJsxKey } from '@utils/ast';
import { useCanDisableChapterInRevision } from './hook.can-disable-chapter-in-revision.js';
import { useCurrentCourseDescriptor } from './hook.current-course-descriptor.js';
import { switchWrapper, contentWrapper, wrapper } from './provider.course-revision-printers.style.js';
import { useCourseRevisionState, extractNodeIdsFromAST, courseRevisionsStore } from './store.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function CourseRevisionPrinterProvider(props) {
  const isEditing = useCourseRevisionState(state => state.isEditing);
  const parentContext = useContentEnginePrinters();
  const interceptor = useCallback((input, rawOutput, context) => {
    const output = parentContext.interceptor?.(input, rawOutput, {
      ...context,
      disableCourseBuilderAddButton: isEditing,
      disableElementToolbar: isEditing
    }) ?? rawOutput;
    const isRelevantNode = context.depth === 0 || ["grid-component", "grid-component.column", "accordion-component", "accordion-component.panel"].includes(context.parentNode?.nodeType ?? "");
    if (!isEditing || !isRelevantNode || !("id" in input) || !input.id) {
      return output;
    }

    // Insert our component override into the ComponentWrapper provided by
    // the <ContentEnginePrinterProvider /> interceptor.
    // This will ensure that the toggle and background is properly contained
    // within the boundaries provided by the PM2 app.
    return /*#__PURE__*/cloneElement(output, {}, [/*#__PURE__*/jsx(CourseRevisionWrapper, {
      nodeId: input.id,
      isWithinLayoutElement: context.parentNode?.nodeType === "grid-component.column",
      children: output.props["children"]
    }, generateJsxKey(context))]);
  }, [isEditing, parentContext]);
  return /*#__PURE__*/jsx(ContentEnginePrinterProvider, {
    printers: courseRevisionPrinters,
    interceptor: interceptor,
    children: props.children
  });
}

/**
 * the course revision does not add any additional printers. Instead it will
 * rely on parent context to provide ContentEngine printers, which the
 * interceptor below will process.
 */
const courseRevisionPrinters = {};
function CourseRevisionWrapper(props) {
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const location = useLocation();
  const previewMode = pm2ConfigStore.useSelector(state => state.previewMode);
  const portalId = pm2ConfigStore.useSelector(state => state.config.id);
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const pageDescriptor = findPageDescriptorByPath(sitemap, location.pathname);
  const ast = usePageAST(pageDescriptor);
  const courseDescriptor = useCurrentCourseDescriptor();
  const canDisableChapter = useCanDisableChapterInRevision(courseDescriptor, pageDescriptor);
  const nodeIds = ast ? extractNodeIdsFromAST(ast) : [];
  const canDisableElement = useCourseRevisionState(state => canDisableChapter || !pageDescriptor ? true : nodeIds.filter(nodeId => !state.disabledIds[pageDescriptor.id]?.[nodeId]).length > 1);
  const isDisabled = useCourseRevisionState(state => {
    if (!pageDescriptor) {
      return false;
    }
    const pageState = state.disabledIds[pageDescriptor.id];
    if (pageState?.disabled) {
      return true;
    }
    return !!pageState?.[props.nodeId];
  });
  const toggleNode = async () => {
    if (!pageDescriptor) {
      return;
    }
    const xml = await getPageContent(portalId, pageDescriptor.id, {
      previewMode
    });
    const ast = await parsePageXML(queryClient, xml, user);
    courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleElement({
      elementId: props.nodeId,
      chapterId: pageDescriptor.id,
      pageAST: ast
    }));
  };
  const label = isDisabled ? "Tilføj indholdselementet til din revision" : "Fjern indholdselementet fra din revision";
  const canToggle = canDisableElement || isDisabled;
  return /*#__PURE__*/jsxs("div", {
    className: cx(wrapper, "revisionWrapper", props.isWithinLayoutElement && "isWithinLayoutElement", isDisabled && "isDisabled"),
    children: [/*#__PURE__*/jsx("div", {
      className: switchWrapper,
      children: /*#__PURE__*/jsx(Tooltip, {
        body: canToggle ? label : "Du skal have mindst ét element tilgængeligt i forløbet",
        ariaType: canToggle ? undefined : "description",
        placement: "top",
        children: ref => /*#__PURE__*/jsx(Switch, {
          ref: ref,
          checked: !isDisabled,
          onChange: toggleNode,
          disabled: !canToggle,
          "aria-label": label
        })
      })
    }), /*#__PURE__*/jsx("div", {
      className: contentWrapper,
      children: props.children
    })]
  });
}
function usePageAST(descriptor) {
  const queryClient = useQueryClient();
  const configState = pm2ConfigStore.useSelector(state => state);
  const user = useUserIdentityState(state => state.user);
  const [ast, setAST] = useState();
  useEffect(() => {
    async function loadAST() {
      if (!descriptor) {
        return;
      }
      const xml = await getPageContent(configState.config.id, descriptor.id, {
        previewMode: configState.previewMode
      });
      const ast = await parsePageXML(queryClient, xml, user);
      setAST(ast);
    }
    loadAST().catch(() => {
      /** no-op */
    });
  }, [configState.config.id, configState.previewMode, descriptor, queryClient, user]);
  return ast;
}

export { CourseRevisionPrinterProvider };
//# sourceMappingURL=provider.course-revision-printers.js.map
